import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { candidateEndpoints } from '@/lib/deployPlan';
import type { Catalog, CatalogStack } from '@/types/catalog';
import type { EndpointWithResources } from '@/types/endpoint';
import type { DomainSummary } from '@/types/domain';

const ENDPOINT_REFRESH_MS = 60_000;
// Optional: without domains the wizard deploys to the default domain, so a failure never blocks it.
const fetchDomains = () => api.domains.list().catch((): DomainSummary[] => []);

interface StacksResult {
  key: string; // `${catalogId}:${attempt}` the result belongs to
  data: CatalogStack[] | null;
  error: string | null;
}

export interface DeployData {
  catalogs: Catalog[];
  endpoints: EndpointWithResources[];
  stacks: CatalogStack[];
  /** Default first; [] until loaded or when loading failed. */
  domains: DomainSummary[];
  /** True until the first catalogs/endpoints result (or error) arrives. */
  loading: boolean;
  /** True while stacks for the chosen catalog have not arrived yet. */
  stacksLoading: boolean;
  /** Catalog/endpoint load error; stale data stays in `catalogs`/`endpoints`. */
  error: string | null;
  stacksError: string | null;
  /** True once catalogs and endpoints both have data (stale or fresh). */
  hasData: boolean;
  /** True while a manual `retry()` of catalogs/endpoints is in flight. */
  retrying: boolean;
  retry: () => void;
  retryStacks: () => void;
}

/** Data for the new-app wizard. Endpoints refresh every 60s; stacks load when `catalogId` changes. */
export function useDeployData(catalogId: number | null): DeployData {
  const catalogsQ = usePolledData(() => api.catalog.list({ show_deleted: false }));
  const endpointsQ = usePolledData(() => api.endpoints.listWithResourcesAll(), ENDPOINT_REFRESH_MS);
  const domainsQ = usePolledData(fetchDomains);

  const catalogs = useMemo(() => (catalogsQ.data ?? []).filter((c) => c.is_active), [catalogsQ.data]);
  const endpoints = useMemo(() => candidateEndpoints(endpointsQ.data ?? []), [endpointsQ.data]);
  const domains = useMemo(() => domainsQ.data ?? [], [domainsQ.data]);

  // Stacks: the effect only starts an async fetch; state is written from the
  // promise callbacks, and a cleanup flag drops responses for a stale
  // (catalogId, attempt) pair. Loading is derived by comparing keys, so a
  // previous catalog's stacks are never exposed for the new one.
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<StacksResult | null>(null);
  const key = `${catalogId}:${attempt}`;

  useEffect(() => {
    if (catalogId === null) return;
    let cancelled = false;
    const k = `${catalogId}:${attempt}`;
    api.catalog.stacks
      .list(catalogId)
      .then((data) => {
        if (!cancelled) setResult({ key: k, data: data.filter((s) => s.is_active), error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) setResult({ key: k, data: null, error: e instanceof Error ? e.message : 'Failed to load stacks' });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogId, attempt]);

  const current = catalogId !== null && result?.key === key ? result : null;
  const stacks = useMemo(() => current?.data ?? [], [current]);

  const { refetch: refetchCatalogs } = catalogsQ;
  const { refetch: refetchEndpoints } = endpointsQ;
  const retry = useCallback(() => {
    refetchCatalogs();
    refetchEndpoints();
  }, [refetchCatalogs, refetchEndpoints]);
  const retryStacks = useCallback(() => setAttempt((a) => a + 1), []);

  return {
    catalogs,
    endpoints,
    stacks,
    domains,
    loading: catalogsQ.loading || endpointsQ.loading,
    stacksLoading: catalogId !== null && current === null,
    error: catalogsQ.error ?? endpointsQ.error,
    stacksError: current?.error ?? null,
    retrying: catalogsQ.refreshing || endpointsQ.refreshing,
    hasData: catalogsQ.data !== undefined && endpointsQ.data !== undefined,
    retry,
    retryStacks,
  };
}
